import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    TextField,
    Tab,
    Tabs,
    Alert,
    Snackbar,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from '@mui/material';
import { Facebook, Search, Comment, ThumbUp, Group } from '@mui/icons-material';
import { getFacebookAccounts } from '../redux/slices/facebookSlice';
import axios from 'axios';

export default function FacebookManager() {
    const dispatch = useDispatch();
    const { accounts } = useSelector((state) => state.facebook);
    const token = localStorage.getItem('token');
    const [selectedAccount, setSelectedAccount] = useState('');
    const [currentTab, setCurrentTab] = useState(0);
    const [postUrl, setPostUrl] = useState('');
    const [groupUrl, setGroupUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Data states
    const [comments, setComments] = useState([]);
    const [likes, setLikes] = useState([]);
    const [members, setMembers] = useState([]);

    useEffect(() => {
        dispatch(getFacebookAccounts());
    }, [dispatch]);

    const handleScrapeComments = async () => {
        if (!selectedAccount || !postUrl) {
            setSnackbar({ open: true, message: 'يرجى اختيار حساب وإدخال رابط المنشور', severity: 'error' });
            return;
        }

        setLoading(true);
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const { data } = await axios.post('https://social-auto-r8we.onrender.com/api/facebook/scrape-post',
                { accountId: selectedAccount, postUrl },
                config
            );
            setComments(data.comments || []);
            setSnackbar({ open: true, message: `✅ تم جمع ${data.comments?.length || 0} تعليق بنجاح!`, severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: err?.response?.data?.message || err?.message || 'فشل جمع التعليقات', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleScrapeLikes = async () => {
        if (!selectedAccount || !postUrl) {
            setSnackbar({ open: true, message: 'يرجى اختيار حساب وإدخال رابط المنشور', severity: 'error' });
            return;
        }

        setLoading(true);
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const { data } = await axios.post('http://localhost:5000/api/facebook/scrape-likes',
                { accountId: selectedAccount, postUrl },
                config
            );
            setLikes(data.likes || []);
            setSnackbar({ open: true, message: `✅ تم جمع ${data.likes?.length || 0} إعجاب بنجاح!`, severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: err?.response?.data?.message || err?.message || 'فشل جمع الإعجابات', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleScrapeMembers = async () => {
        if (!selectedAccount || !groupUrl) {
            setSnackbar({ open: true, message: 'يرجى اختيار حساب وإدخال رابط المجموعة', severity: 'error' });
            return;
        }

        setLoading(true);
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const { data } = await axios.post('https://social-auto-r8we.onrender.com/api/facebook/scrape-members',
                { accountId: selectedAccount, groupUrl },
                config
            );
            setMembers(data.members || []);
            setSnackbar({ open: true, message: `✅ تم جمع ${data.members?.length || 0} عضو بنجاح!`, severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: err?.response?.data?.message || err?.message || 'فشل جمع الأعضاء', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Box sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: 3, p: { xs: 3, md: 4 }, mb: 3, color: 'white' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Facebook sx={{ fontSize: { xs: 32, md: 40 } }} />
                        <Box>
                            <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                                إدارة Facebook
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>جمع البيانات من Facebook</Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Card sx={{ boxShadow: 3 }}>
                        <CardContent>
                            <FormControl fullWidth sx={{ mb: 3 }}>
                                <InputLabel>اختر حساب Facebook</InputLabel>
                                <Select
                                    value={selectedAccount}
                                    onChange={(e) => setSelectedAccount(e.target.value)}
                                    label="اختر حساب Facebook"
                                >
                                    {accounts.map((account) => (
                                        <MenuItem key={account._id} value={account._id}>
                                            {account.name || account._id}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Tabs
                                value={currentTab}
                                onChange={(e, v) => setCurrentTab(v)}
                                sx={{ mb: 3 }}
                                variant="scrollable"
                                scrollButtons="auto"
                                allowScrollButtonsMobile
                            >
                                <Tab icon={<Comment />} label="التعليقات" />
                                <Tab icon={<ThumbUp />} label="الإعجابات" />
                                <Tab icon={<Group />} label="الأعضاء" />
                            </Tabs>

                            {/* Comments Tab */}
                            {currentTab === 0 && selectedAccount && (
                                <Box>
                                    <Typography variant="h6" gutterBottom>جمع تعليقات المنشور</Typography>
                                    <TextField
                                        fullWidth
                                        label="رابط المنشور"
                                        placeholder="https://www.facebook.com/..."
                                        value={postUrl}
                                        onChange={(e) => setPostUrl(e.target.value)}
                                        sx={{ mb: 3 }}
                                    />
                                    <Button
                                        variant="contained"
                                        startIcon={<Search />}
                                        onClick={handleScrapeComments}
                                        disabled={loading || !postUrl}
                                        sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                                    >
                                        {loading ? 'جاري الجمع...' : 'جمع التعليقات'}
                                    </Button>

                                    {comments.length > 0 && (
                                        <TableContainer component={Paper}>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>المستخدم</TableCell>
                                                        <TableCell>التعليق</TableCell>
                                                        <TableCell>الرابط</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {comments.map((comment, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>{comment.author || 'Unknown'}</TableCell>
                                                            <TableCell>{comment.text}</TableCell>
                                                            <TableCell>
                                                                {comment.link && (
                                                                    <a href={comment.link} target="_blank" rel="noopener noreferrer">
                                                                        رابط
                                                                    </a>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </Box>
                            )}

                            {/* Likes Tab */}
                            {currentTab === 1 && selectedAccount && (
                                <Box>
                                    <Typography variant="h6" gutterBottom>جمع إعجابات المنشور</Typography>
                                    <TextField
                                        fullWidth
                                        label="رابط المنشور"
                                        placeholder="https://www.facebook.com/..."
                                        value={postUrl}
                                        onChange={(e) => setPostUrl(e.target.value)}
                                        sx={{ mb: 3 }}
                                    />
                                    <Button
                                        variant="contained"
                                        startIcon={<ThumbUp />}
                                        onClick={handleScrapeLikes}
                                        disabled={loading || !postUrl}
                                        sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                                    >
                                        {loading ? 'جاري الجمع...' : 'جمع الإعجابات'}
                                    </Button>

                                    {likes.length > 0 && (
                                        <TableContainer component={Paper}>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>الاسم</TableCell>
                                                        <TableCell>الرابط</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {likes.map((like, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>{like.name || 'Unknown'}</TableCell>
                                                            <TableCell>
                                                                {like.link && (
                                                                    <a href={like.link} target="_blank" rel="noopener noreferrer">
                                                                        رابط
                                                                    </a>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </Box>
                            )}

                            {/* Members Tab */}
                            {currentTab === 2 && selectedAccount && (
                                <Box>
                                    <Typography variant="h6" gutterBottom>جمع أعضاء المجموعة</Typography>
                                    <TextField
                                        fullWidth
                                        label="رابط المجموعة"
                                        placeholder="https://www.facebook.com/groups/..."
                                        value={groupUrl}
                                        onChange={(e) => setGroupUrl(e.target.value)}
                                        sx={{ mb: 3 }}
                                    />
                                    <Button
                                        variant="contained"
                                        startIcon={<Group />}
                                        onClick={handleScrapeMembers}
                                        disabled={loading || !groupUrl}
                                        sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                                    >
                                        {loading ? 'جاري الجمع...' : 'جمع الأعضاء'}
                                    </Button>

                                    {members.length > 0 && (
                                        <TableContainer component={Paper}>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>الاسم</TableCell>
                                                        <TableCell>الرابط</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {members.map((member, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>{member.name || 'Unknown'}</TableCell>
                                                            <TableCell>
                                                                {member.link && (
                                                                    <a href={member.link} target="_blank" rel="noopener noreferrer">
                                                                        رابط
                                                                    </a>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
